import React, { useEffect, useState } from "react";
import axios from "axios";
import { Sprout } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [tab, setTab] = useState("pitches");
  const [pitches, setPitches] = useState([]);
  const [invests, setInvests] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [p, i] = await Promise.all([
        axios.get(`${API}/pitch`),
        axios.get(`${API}/invest`),
      ]);
      setPitches(p.data);
      setInvests(i.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen bg-[#F7F8EF]">
      <header className="bg-[#14401F] text-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3" data-testid="admin-home-link">
            <img
              src="/sparkseed-logo-clean.png"
              alt="SparkSeed Ventures"
              className="h-9 w-auto"
              style={{ filter: "brightness(1.1)" }}
            />
            <span className="font-serif-display text-sm font-semibold opacity-70">
              · Admin
            </span>
          </a>
          <button
            data-testid="admin-refresh"
            onClick={load}
            className="text-sm px-4 py-2 rounded-full border border-white/20 hover:bg-white/10"
          >
            Refresh
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
        <h1 className="font-serif-display text-4xl font-bold text-[#0F2818] mb-2">
          Submissions
        </h1>
        <p className="text-[#4A5A4F] mb-8">Inbound from founders & investors.</p>

        <div className="inline-flex p-1.5 bg-[#DCE8C9] rounded-full border border-[#14401F]/10 mb-8">
          <button
            data-testid="admin-tab-pitches"
            onClick={() => setTab("pitches")}
            className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${
              tab === "pitches" ? "bg-[#14401F] text-white" : "text-[#0F2818]/70"
            }`}
          >
            Founder Pitches ({pitches.length})
          </button>
          <button
            data-testid="admin-tab-invests"
            onClick={() => setTab("invests")}
            className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${
              tab === "invests" ? "bg-[#14401F] text-white" : "text-[#0F2818]/70"
            }`}
          >
            Investor Inquiries ({invests.length})
          </button>
        </div>

        {loading && <div className="text-[#4A5A4F]">Loading…</div>}

        {!loading && tab === "pitches" && (
          <div className="space-y-3">
            {pitches.length === 0 && <Empty label="No pitches yet." />}
            {pitches.map((p, i) => (
              <div key={p.id} data-testid={`pitch-row-${i}`} className="bg-white rounded-2xl border border-[#14401F]/10 p-5">
                <div className="flex flex-wrap items-baseline justify-between gap-3 mb-3">
                  <div>
                    <div className="font-serif-display text-xl font-bold text-[#0F2818]">{p.startup_name}</div>
                    <div className="text-sm text-[#4A5A4F]">{p.founder_name} · {p.city}</div>
                  </div>
                  <div className="flex gap-2">
                    <Tag>{p.sector}</Tag>
                    <Tag>{p.stage}</Tag>
                  </div>
                </div>
                <div className="text-sm text-[#0F2818]/80 space-y-1">
                  <div><b>Email:</b> {p.email}</div>
                  {p.traction && <div><b>Traction:</b> {p.traction}</div>}
                  {p.deck_url && <div><b>Deck:</b> <a className="text-[#14401F] underline" href={p.deck_url} target="_blank" rel="noreferrer">{p.deck_url}</a></div>}
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && tab === "invests" && (
          <div className="space-y-3">
            {invests.length === 0 && <Empty label="No investor inquiries yet." />}
            {invests.map((iv, i) => (
              <div key={iv.id} data-testid={`invest-row-${i}`} className="bg-white rounded-2xl border border-[#14401F]/10 p-5">
                <div className="flex flex-wrap items-baseline justify-between gap-3 mb-3">
                  <div>
                    <div className="font-serif-display text-xl font-bold text-[#0F2818]">{iv.name}</div>
                    {iv.entity && <div className="text-sm text-[#4A5A4F]">{iv.entity}</div>}
                  </div>
                  <Tag>{iv.ticket_size}</Tag>
                </div>
                <div className="text-sm text-[#0F2818]/80 space-y-1">
                  <div><b>Email:</b> {iv.email}</div>
                  {iv.message && <div><b>Message:</b> {iv.message}</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

const Tag = ({ children }) => (
  <span className="text-xs px-3 py-1 rounded-full bg-[#DCE8C9] text-[#14401F] font-semibold uppercase tracking-wider">
    {children}
  </span>
);

const Empty = ({ label }) => (
  <div className="rounded-2xl border border-dashed border-[#14401F]/20 p-10 text-center text-[#4A5A4F]">
    {label}
  </div>
);
